import type { TOutreachCandidate } from 'types/outreachCandidates.types';
import type { VoiceConversation, ParsedQuestionItem } from 'rtk/query/apis/voiceConversation';
import type { OutreachCampaignDto, OutreachInterviewTemplateDto } from 'rtk/query/apis/outreach';

export const generateSingleCandidateCSV = (
  candidate: TOutreachCandidate,
  campaign?: OutreachCampaignDto | null,
  voiceConversations?: VoiceConversation[],
  template?: OutreachInterviewTemplateDto | null,
): string => {
  const rows: string[][] = [];

  // Helper to add a row
  const addRow = (field: string, value: string | number | undefined | null) => {
    // Escape quotes in value
    const safeValue = value === undefined || value === null ? '' : String(value).replace(/"/g, '""');
    rows.push([field, `"${safeValue}"`]);
  };

  const addHeader = (title: string) => {
    rows.push([`--- ${title} ---`, '']);
  };

  const voiceConversation =
    voiceConversations && voiceConversations.length > 0 ? voiceConversations[voiceConversations.length - 1] : undefined;

  const analysis = voiceConversation?.analysis;

  // Header
  rows.push(['Field', 'Value']);

  // --- CANDIDATE INFORMATION ---
  addHeader('CANDIDATE INFORMATION');
  addRow('Candidate Name', `${candidate.firstName} ${candidate.lastName}`);
  addRow('ID', candidate.id);
  let role = '-';
  if (template) {
    const langConfig = template.languages.find((l) => l.language === candidate.language);
    if (langConfig) role = langConfig.role;
  }
  addRow('Role', role);
  addRow('Campaign', campaign?.name || '-');
  addRow('Phone', candidate.phone ? candidate.phone : '-');
  addRow('Status', candidate.callStatus || 'pending');
  addRow('Outcome', candidate.qualificationStatus || '-');

  const callTime = voiceConversation?.createdAt ? voiceConversation.createdAt.substring(0, 16).replace('T', ' ') : '-';

  addRow('Call Time', callTime);

  const durationSeconds = voiceConversation?.callDuration;
  const durationStr = durationSeconds
    ? `${Math.floor(durationSeconds / 60)
        .toString()
        .padStart(2, '0')}:${(durationSeconds % 60).toString().padStart(2, '0')}`
    : '-';
  addRow('Duration', durationStr);

  // --- ANALYSIS SUMMARY ---
  addHeader('ANALYSIS SUMMARY');
  addRow('Executive Summary', analysis?.transcriptSummary || '-');
  addRow('Final Decision', analysis?.callSuccessful || '-');

  addRow('Decision Rationale', '-'); // Placeholder

  // --- DATA CAPTURED ---
  addHeader('DATA CAPTURED');
  addRow('Language Used', voiceConversation?.languageUsed || candidate.language);
  addRow('Consent Given', voiceConversation?.consentGiven ? 'TRUE' : 'FALSE');

  // --- QUESTIONS & ANSWERS ---
  addHeader('QUESTIONS & ANSWERS');
  if (voiceConversation?.questions) {
    voiceConversation.questions.forEach((q: ParsedQuestionItem, index: number) => {
      const num = index + 1;
      const qPrefix = `Q${num}`;

      let answerText = '-';
      if (q.answer !== null && q.answer.raw !== '') {
        if ('iso' in q.answer) {
          answerText = q.answer.iso ?? q.answer.raw;
        } else {
          answerText = typeof q.answer.value === 'boolean' ? (q.answer.value ? 'Yes' : 'No') : q.answer.value;
        }
      }

      addRow(`${qPrefix} Question`, q.text);
      addRow(`${qPrefix} Answer`, answerText);
      addRow(`${qPrefix} Required`, q.condition === 'required' ? 'Yes' : 'No');
    });
  }

  return rows.map((r) => r.join(',')).join('\n');
};
